// src/pages/patient/PatientDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { patientDashboardApi } from "../../services/patientDashboard";
import AppointmentAttention from "../../components/patient/notifications/AppointmentAttention";

function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "calendar":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M7 3v3M17 3v3M4 8h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <rect x="4" y="6" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="2.2" />
        </svg>
      );

    case "ticket":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 1 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 1 0 0-4V8Z" stroke="currentColor" strokeWidth="2.1" strokeLinejoin="round" />
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
        </svg>
      );

    case "activity":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M4 13h4l2-7 4 14 2-7h4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "money":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2.1" />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2.1" />
          <path d="M7 10h.01M17 14h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      );

    case "refresh":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "arrow":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );

    case "play":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M8 5v14l11-7L8 5Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
        </svg>
      );

    case "warning":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none">
          <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M10.3 4.9 2.8 18a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="2.2" />
        </svg>
      );

    default:
      return null;
  }
}

function Button({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={classNames(
        "inline-flex items-center justify-center gap-2 rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-semibold transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60",
        className
      )}
    >
      {children}
    </button>
  );
}

function PrimaryLink({ children, className = "", ...props }) {
  return (
    <Link
      {...props}
      className={classNames(
        "btn-primary-gradient inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold text-white transition hover:shadow-soft",
        className
      )}
    >
      {children}
    </Link>
  );
}

function Pill({ children, tone = "neutral", className = "" }) {
  const map = {
    info: "border-sky-200 bg-sky-50 text-sky-700",
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    warn: "border-amber-200 bg-amber-50 text-amber-700",
    danger: "border-rose-200 bg-rose-50 text-rose-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
  };

  return (
    <span className={classNames("inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize", map[tone] || map.neutral, className)}>
      {children}
    </span>
  );
}

function SectionCard({ title, subtitle, right, children, className = "" }) {
  return (
    <div className={classNames("overflow-hidden rounded-3xl border border-border bg-white shadow-soft", className)}>
      <div className="flex items-start justify-between gap-3 border-b border-border bg-surface p-5">
        <div>
          <div className="text-sm font-semibold text-slate-950">{title}</div>
          {subtitle ? <div className="mt-1 text-xs text-muted">{subtitle}</div> : null}
        </div>
        {right ? <div>{right}</div> : null}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function pretty(value) {
  if (!value) return "—";
  return String(value).replace(/_/g, " ");
}

function formatDateTime(value, timezone = "Asia/Kolkata") {
  if (!value) return "—";

  try {
    return new Intl.DateTimeFormat("en-IN", {
      timeZone: timezone,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function formatMoney(value, currency = "USD") {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));
  } catch {
    return `${currency || "USD"} ${value || 0}`;
  }
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();

  if (["booked", "confirmed", "open"].includes(s)) return "info";
  if (["checked_in", "in_progress"].includes(s)) return "violet";
  if (["completed", "resolved", "closed"].includes(s)) return "success";
  if (["cancelled_by_patient", "cancelled_by_clinic", "cancelled_by_doctor"].includes(s)) return "danger";
  if (["no_show"].includes(s)) return "warn";

  return "neutral";
}

function promotionTheme(theme) {
  const map = {
    blue: "from-blue-600 via-sky-500 to-cyan-500",
    emerald: "from-emerald-600 via-teal-500 to-cyan-500",
    violet: "from-violet-600 via-purple-500 to-fuchsia-500",
    amber: "from-amber-500 via-orange-500 to-rose-500",
    rose: "from-rose-600 via-pink-500 to-orange-500",
    slate: "from-slate-800 via-slate-700 to-slate-600",
  };

  return map[theme] || map.blue;
}

// function MetricCard({ title, value, subtitle, icon, tone = "info" }) {
//   const style =
//     tone === "success"
//       ? "border-emerald-100 bg-emerald-50 text-emerald-700"
//       : tone === "danger"
//       ? "border-rose-100 bg-rose-50 text-rose-700"
//       : tone === "warn"
//       ? "border-amber-100 bg-amber-50 text-amber-700"
//       : tone === "violet"
//       ? "border-violet-100 bg-violet-50 text-violet-700"
//       : "border-sky-100 bg-sky-50 text-sky-700";

//   return (
//     <div className={classNames("rounded-3xl border p-5", style)}>
//       <div className="flex items-start justify-between gap-4">
//         <div>
//           <div className="text-xs font-semibold uppercase tracking-wide opacity-75">{title}</div>
//           <div className="mt-2 text-2xl font-semibold">{value}</div>
//           {subtitle ? <div className="mt-1 text-xs opacity-75">{subtitle}</div> : null}
//         </div>

//         <div className="rounded-2xl bg-white/70 p-3">
//           <Icon name={icon} className="h-5 w-5" />
//         </div>
//       </div>
//     </div>
//   );
// }

function MetricCard({ title, value, subtitle, icon, tone = "info" }) {
  const style =
    tone === "success"
      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
      : tone === "danger"
      ? "border-rose-100 bg-rose-50 text-rose-700"
      : tone === "warn"
      ? "border-amber-100 bg-amber-50 text-amber-700"
      : tone === "violet"
      ? "border-violet-100 bg-violet-50 text-violet-700"
      : "border-sky-100 bg-sky-50 text-sky-700";

  return (
    <div className={classNames("rounded-3xl border p-5", style)}>
      <div className="flex h-full items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide opacity-75">
            {title}
          </div>
          <div className="mt-2 text-2xl font-semibold leading-none">
            {value}
          </div>
          {subtitle ? (
            <div className="mt-2 text-xs leading-5 opacity-75">
              {subtitle}
            </div>
          ) : null}
        </div>

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/75 shadow-sm">
          <Icon name={icon} className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function DonutChart({ items = [] }) {
  const total = items.reduce((sum, item) => sum + Number(item.count || 0), 0);

  const colors = {
    open: "#0EA5E9",
    in_progress: "#F59E0B",
    resolved: "#10B981",
    closed: "#64748B",
  };

  let offset = 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;

  if (!total) {
    return (
      <div className="flex h-[220px] items-center justify-center rounded-3xl bg-slate-50 text-sm font-semibold text-muted">
        No tickets yet
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 items-center gap-5 md:grid-cols-[220px_1fr]">
      <div className="relative mx-auto h-[220px] w-[220px]">
        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#E2E8F0" strokeWidth="16" />

          {items.map((item) => {
            const value = Number(item.count || 0);
            const dash = (value / total) * circumference;
            const strokeDasharray = `${dash} ${circumference - dash}`;
            const strokeDashoffset = -offset;

            offset += dash;

            return (
              <circle
                key={item.status}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={colors[item.status] || "#CBD5E1"}
                strokeWidth="16"
                strokeLinecap="round"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
              />
            );
          })}
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-semibold text-slate-950">{total}</div>
          <div className="text-xs font-semibold text-muted">Tickets</div>
        </div>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.status} className="flex items-center justify-between rounded-2xl border border-border bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: colors[item.status] || "#CBD5E1" }}
              />
              <div className="text-sm font-semibold text-slate-800">{item.label}</div>
            </div>
            <div className="text-sm font-semibold text-slate-950">{item.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PromotionCard({ promotion }) {
  const isVideo = promotion.media_type === "video" && promotion.media_url;
  const isImage = promotion.media_type === "image" && promotion.media_url;

  return (
    <div className={classNames("overflow-hidden rounded-3xl bg-gradient-to-br p-[1px] shadow-soft", promotionTheme(promotion.theme))}>
      <div className="overflow-hidden rounded-3xl bg-white">
        {isImage ? (
          <div className="relative h-52 overflow-hidden">
            <img
              src={promotion.media_url}
              alt={promotion.media_alt_text || promotion.title}
              className="h-full w-full object-cover"
            />
            {promotion.badge_text ? (
              <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-800">
                {promotion.badge_text}
              </div>
            ) : null}
          </div>
        ) : null}

        {isVideo ? (
          <div className="relative h-52 overflow-hidden bg-slate-950">
            <video
              src={promotion.media_url}
              poster={promotion.media_thumbnail_url || undefined}
              className="h-full w-full object-cover"
              muted
              controls
            />
            <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-800">
              <Icon name="play" className="mr-1 inline h-3 w-3" />
              {promotion.badge_text || "Video"}
            </div>
          </div>
        ) : null}

        <div className="p-5">
          {!isImage && !isVideo && promotion.badge_text ? (
            <Pill tone="info">{promotion.badge_text}</Pill>
          ) : null}

          <h3 className="mt-3 text-xl font-semibold text-slate-950">{promotion.title}</h3>

          {promotion.subtitle ? (
            <div className="mt-2 text-sm font-semibold text-slate-600">
              {promotion.subtitle}
            </div>
          ) : null}

          {promotion.body ? (
            <p className="mt-3 text-sm leading-6 text-muted">{promotion.body}</p>
          ) : null}

          {promotion.cta_label && promotion.cta_url ? (
            <Link
              to={promotion.cta_url}
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary"
            >
              {promotion.cta_label}
              <Icon name="arrow" className="h-4 w-4" />
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle, icon = "calendar" }) {
  return (
    <div className="rounded-3xl border border-dashed border-border bg-surface p-8 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-muted">
        <Icon name={icon} className="h-7 w-7" />
      </div>
      <div className="mt-4 text-sm font-semibold text-slate-950">{title}</div>
      <div className="mt-1 text-sm text-muted">{subtitle}</div>
    </div>
  );
}

export default function PatientDashboard() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [activePromotion, setActivePromotion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);
  const activeRequest = useRef(null);

  const upcoming = data?.upcoming_week || {};
  const services = data?.services_this_month || {};
  const tickets = data?.tickets || {};
  const care = data?.care_summary || {};
  const promotions = data?.promotions || [];
  const hasAmountDue = (services.money_by_currency || []).length
    ? (services.money_by_currency || []).some((item) => Number(item.total_due || 0) > 0)
    : Number(services.total_due || 0) > 0;

  const heroPromotion = promotions[activePromotion] || promotions[0];

  const ticketHealth = useMemo(() => {
    const active = Number(tickets.open || 0) + Number(tickets.in_progress || 0);
    if (active === 0) return "All clear";
    if (active === 1) return "1 active ticket";
    return `${active} active tickets`;
  }, [tickets]);

  useEffect(() => {
    loadDashboard();
    return () => activeRequest.current?.abort();
    // Initial load only; the Refresh button starts subsequent requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadDashboard() {
    const sequence = ++requestSeq.current;
    activeRequest.current?.abort();
    const controller = new AbortController();
    activeRequest.current = controller;
    setLoading(true);
    setError("");

    try {
      const res = await patientDashboardApi.summary({ signal: controller.signal });
      if (sequence === requestSeq.current) setData(res);
    } catch (e) {
      if (e?.name !== "AbortError" && sequence === requestSeq.current) {
        setError(e.message || "Failed to load dashboard.");
      }
    } finally {
      if (sequence === requestSeq.current) setLoading(false);
    }
  }

  if (loading && !data) {
    return (
      <main className="min-h-screen bg-surface">
        <div className="mx-auto max-w-[1600px] px-4 py-6 lg:px-6">
          <div className="rounded-3xl border border-border bg-white p-12 text-center shadow-soft">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-primary" />
            <div className="mt-4 text-sm font-semibold text-muted">Loading dashboard...</div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-surface">
      <div className="mx-auto max-w-[1600px] px-4 py-6 lg:px-6">
        <div className="mb-6 flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <div className="text-3xl font-semibold tracking-tight text-slate-950">
              Patient dashboard
            </div>
            <div className="mt-1 text-sm text-muted">
              Your appointments, services, support activity, and care updates in one place.
            </div>
            {data?.generated_at ? (
              <div className="mt-2 text-xs font-semibold text-slate-400">
                Last updated {formatDateTime(data.generated_at)}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={loadDashboard} disabled={loading}>
              <Icon name="refresh" className="h-4 w-4" />
              Refresh
            </Button>

            <PrimaryLink to="/patient/book-appointment">
              Book appointment
            </PrimaryLink>
          </div>
        </div>

        <AppointmentAttention />

        {error ? (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}

        <div
          className="overflow-hidden rounded-3xl border border-border shadow-soft"
          style={{
            background:
              "linear-gradient(135deg, rgba(var(--c-primary),0.12), rgba(var(--c-accent),0.12), rgba(255,255,255,0.90))",
          }}
        >
          <div className="grid grid-cols-1 gap-5 p-5 xl:grid-cols-[1.4fr_0.9fr]">
            <div className="rounded-3xl bg-white/80 p-6 backdrop-blur">
              <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-start">
                <div>
                  <Pill tone="info">This week</Pill>
                  <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950">
                    {upcoming.next_appointment
                      ? "Your next appointment is coming up"
                      : "No upcoming appointment this week"}
                  </h1>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-muted">
                    {upcoming.next_appointment
                      ? `${upcoming.next_appointment.service_name} with ${upcoming.next_appointment.doctor_name} on ${formatDateTime(
                          upcoming.next_appointment.start_at,
                          upcoming.next_appointment.timezone
                        )}.`
                      : "You can book a new appointment or continue managing your existing care records."}
                  </p>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <PrimaryLink to="/patient/appointments">
                      View appointments
                      <Icon name="arrow" className="h-4 w-4" />
                    </PrimaryLink>

                    <Link
                      to="/patient/tickets/new"
                      className="inline-flex items-center justify-center rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-semibold transition hover:bg-slate-50"
                    >
                      Raise support ticket
                    </Link>
                  </div>
                </div>

                <div className="grid min-w-[300px] grid-cols-1 gap-3">
                  <MetricCard
                    title="Appointments"
                    value={upcoming.total || 0}
                    subtitle="Next 7 days"
                    icon="calendar"
                    tone="info"
                  />
                  <MetricCard
                    title="Forms pending"
                    value={upcoming.pending_forms || 0}
                    subtitle="Required forms"
                    icon="warning"
                    tone={Number(upcoming.pending_forms || 0) > 0 ? "warn" : "success"}
                  />
                </div>
              </div>
            </div>

            {heroPromotion ? (
              <PromotionCard promotion={heroPromotion} />
            ) : (
              <div className="rounded-3xl border border-dashed border-border bg-white/80 p-6">
                <Pill tone="violet">Promotion</Pill>
                <div className="mt-4 text-xl font-semibold text-slate-950">
                  Promotions will appear here
                </div>
                <div className="mt-2 text-sm leading-6 text-muted">
                  This section is ready for admin-configured images, videos, offers, health tips, and service promotions.
                </div>
              </div>
            )}
          </div>

          {promotions.length > 1 ? (
            <div className="flex flex-wrap gap-2 border-t border-white/60 bg-white/50 px-5 py-4">
              {promotions.map((promotion, idx) => (
                <button
                  key={promotion.id}
                  onClick={() => setActivePromotion(idx)}
                  className={classNames(
                    "rounded-full px-3 py-1 text-xs font-semibold transition",
                    idx === activePromotion
                      ? "bg-primary text-white"
                      : "border border-border bg-white text-muted hover:bg-slate-50"
                  )}
                >
                  {promotion.badge_text || promotion.title}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <MetricCard
            title="Upcoming week"
            value={upcoming.total || 0}
            subtitle={`${upcoming.online || 0} online · ${upcoming.in_clinic || 0} clinic`}
            icon="calendar"
            tone="info"
          />

          <MetricCard
            title="Services this month"
            value={services.total_services || 0}
            subtitle={`${services.unique_services || 0} unique services`}
            icon="activity"
            tone="violet"
          />

          <MetricCard
            title="Past appointments"
            value={care.past_appointments || 0}
            subtitle="Your care history"
            icon="activity"
            tone="success"
          />

          <MetricCard
            title="Connected doctors"
            value={care.connected_doctors || 0}
            subtitle="Doctors in your care history"
            icon="activity"
            tone="info"
          />

          <MetricCard
            title="Amount due"
            value={(services.money_by_currency || []).length
              ? (services.money_by_currency || [])
                  .map((item) => formatMoney(item.total_due || 0, item.currency))
                  .join(" · ")
              : formatMoney(services.total_due || 0, services.currency === "MULTI" ? "USD" : services.currency)}
            subtitle={`${services.month_label || "This month"}`}
            icon="money"
            tone={hasAmountDue ? "warn" : "success"}
          />

          <MetricCard
            title="Support"
            value={ticketHealth}
            subtitle={`${tickets.urgent_open || 0} urgent active`}
            icon="ticket"
            tone={Number(tickets.urgent_open || 0) > 0 ? "danger" : "success"}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
          <section className="xl:col-span-7">
            <SectionCard
              title="Upcoming appointments"
              subtitle="Summary of appointments scheduled for the next 7 days."
              right={
                <Link to="/patient/appointments" className="text-sm font-semibold text-primary">
                  View all
                </Link>
              }
            >
              {(upcoming.items || []).length === 0 ? (
                <EmptyState
                  icon="calendar"
                  title="No appointments this week"
                  subtitle="Book an appointment when you are ready."
                />
              ) : (
                <div className="space-y-3">
                  {(upcoming.items || []).slice(0, 6).map((appt) => (
                    <button
                      key={appt.id}
                      type="button"
                      onClick={() => navigate(`/patient/appointments/${appt.id}`)}
                      className="w-full rounded-3xl border border-border bg-white p-4 text-left transition hover:bg-slate-50 hover:shadow-soft"
                    >
                      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                        <div>
                          <div className="font-semibold text-slate-950">{appt.service_name}</div>
                          <div className="mt-1 text-sm text-muted">
                            {appt.doctor_name} · {appt.clinic_name || "Clinic"}
                          </div>
                          <div className="mt-2 text-sm font-semibold text-slate-700">
                            {formatDateTime(appt.start_at, appt.timezone)}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 sm:justify-end">
                          <Pill tone={statusTone(appt.status)}>{pretty(appt.status)}</Pill>
                          <Pill tone={appt.consultation_mode === "video" ? "violet" : "info"}>
                            {pretty(appt.consultation_mode)}
                          </Pill>
                          {Number(appt.pending_required_forms_count || 0) > 0 ? (
                            <Pill tone="warn">{appt.pending_required_forms_count} forms</Pill>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </SectionCard>
          </section>

          <aside className="xl:col-span-5">
            <SectionCard
              title="Support ticket tracking"
              subtitle="Overall support ticket status."
              right={
                <Link to="/patient/tickets" className="text-sm font-semibold text-primary">
                  Open tickets
                </Link>
              }
            >
              <DonutChart items={tickets.items || []} />
            </SectionCard>
          </aside>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-12">
          <section className="xl:col-span-12">
            <SectionCard
              title={`Services consumed · ${services.month_label || "This month"}`}
              subtitle="Services consumed by this patient during the current month."
              right={
                <Link to="/patient/invoices" className="text-sm font-semibold text-primary">
                  View invoices
                </Link>
              }
            >
              {(services.items || []).length === 0 ? (
                <EmptyState
                  icon="activity"
                  title="No services consumed this month"
                  subtitle="Completed and active services will appear here."
                />
              ) : (
                <div className="space-y-3">
                  {(services.items || []).map((item, idx) => {
                    const max = Math.max(...(services.items || []).map((x) => Number(x.appointment_count || 0)), 1);
                    const width = `${Math.max(8, (Number(item.appointment_count || 0) / max) * 100)}%`;

                    return (
                      <div key={`${item.service_name}-${idx}`} className="rounded-3xl border border-border bg-white p-4">
                        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-950">
                              {item.service_name}
                            </div>
                            <div className="mt-1 text-xs text-muted">
                              {item.doctor_name || "Doctor"} · {item.clinic_name || "Clinic"} · Latest {formatDateTime(item.latest_visit_at)}
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3 text-right">
                            <div>
                              <div className="text-xs text-muted">Visits</div>
                              <div className="font-semibold">{item.appointment_count}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted">Billed</div>
                              <div className="font-semibold">{formatMoney(item.total_billed, item.currency)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-muted">Due</div>
                              <div className="font-semibold text-amber-700">{formatMoney(item.total_due, item.currency)}</div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-primary" style={{ width }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          </section>

          <aside className="xl:col-span-12">
            <SectionCard
              title="Promotions"
              subtitle=""
            >
              {promotions.length === 0 ? (
                <EmptyState
                  icon="activity"
                  title="No active promotions"
                  subtitle="Promotions configured by admin will appear here."
                />
              ) : (
                <div className="space-y-4">
                  {promotions.slice(0, 3).map((promotion) => (
                    <PromotionCard key={promotion.id} promotion={promotion} />
                  ))}
                </div>
              )}
            </SectionCard>
          </aside>
        </div>
      </div>
    </main>
  );
}
